import './App.css'
import 'ckeditor5/ckeditor5.css'

import { InMemoryCache } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { relayStylePagination } from '@apollo/client/utilities'
import { subscriptionClient as apolloClient } from '@curbside-health-inc/client'
import { EnvWindow } from '@curbside-health-inc/constants'
import { registerPathwayV2ErrorReporter } from '@curbside-health-inc/pathway-v2/logger'
import { registerCkPreviewHostStyles } from '@curbside-health-inc/pathway-v2/viewers/ckPreviewLoader'
import { currentFrontendApp } from '@curbside-health-inc/reactive-vars'
import { ToastProvider } from '@curbside-health-inc/ui'
import loadable from '@loadable/component'
import CssBaseline from '@mui/material/CssBaseline'
import GlobalStyles from '@mui/material/GlobalStyles'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { Provider, useRollbar } from '@rollbar/react'
import ServerHealth from 'components/ServerHealth'
import { INSTITUTION_PAGE_KEY_ARGS } from 'lib/institutionPageKeyArgs'
import installPathwayLockLink from 'lib/pathwayLockLink'
import rollbarCheckIgnore from 'lib/rollbarCheckIgnore'
import rollbarIgnoredMessages from 'lib/rollbarIgnoredMessages'
import React, { useEffect } from 'react'
import { BrowserRouter as Router } from 'react-router'
import Rollbar from 'rollbar'
import cecmDefaultTheme, { MuiSevenGlobalStyles } from 'Themes/MuiSeven'
import { registerErrorReporter } from 'util/logger'

// CE-10579. Every request carries this tab's pathway edit lock signature, so the holder's
// own saves are accepted and nobody else's are. Installed here rather than in
// `@curbside-health-inc/client` because the client is shared with frontends that have no
// pathway editor; done once, at module load, before anything has issued a request.
installPathwayLockLink(apolloClient)

// The v2 evidence viewers only carry a scoped copy of this global sheet; keep loading the original.
registerCkPreviewHostStyles(() => import('components/CurbsideCKEditorPreview/styles.css'))

// `@curbside-health-inc/client` pages `institutionPageCombined` with a keyless
// `relayStylePagination()`, so every institution, filter, sort and search shares one
// list: a "load more" that lands after a filter change is appended to the new
// filter's results and overwrites its `totalCount` and cursor. Key the list by
// everything except the page arguments (`INSTITUTION_PAGE_KEY_ARGS`).
;(apolloClient.cache as InMemoryCache).policies.addTypePolicies({
  Query: {
    fields: {
      institutionPageCombined: relayStylePagination(INSTITUTION_PAGE_KEY_ARGS),
    },
  },
})

const CurbsideRouter = loadable(() => import('./routing/Router'))

declare let window: EnvWindow

if (window._env_.ENABLE_APOLLO_DEV_MESSAGES === 'true') {
  // Adds messages only in a dev environment
  import('@apollo/client/dev').then(({ loadDevMessages, loadErrorMessages }) => {
    loadDevMessages()
    loadErrorMessages()
  })
}

currentFrontendApp('CECM')
const rollbarConfig: Rollbar.Configuration = {
  accessToken: window._env_.ROLLBAR_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  checkIgnore: rollbarCheckIgnore,
  environment: window._env_.ROLLBAR_ENV,
  enabled: window._env_.ROLLBAR_ENABLED === 'true',
  ignoredMessages: rollbarIgnoredMessages,
  payload: {
    client: {
      javascript: {
        code_version: window._env_.VERSION || '1.0.0',
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },
}
/**
 * Hands the provider's own Rollbar to `logError`, so caught errors reach the same place
 * uncaught ones do. Reads the instance out of context rather than constructing one and
 * passing it as `instance` — `Provider`'s `invariant(isRollbarInstance(instance))` rejects
 * an instance built from our own `rollbar` import (a different copy than `@rollbar/react`
 * checks against) and would crash the app. Same cross-copy identity trap as
 * `v2/document/errors.ts`'s `ApolloError` handling.
 */
const ReportCaughtErrors: React.FC = () => {
  const rollbar = useRollbar()

  useEffect(() => {
    registerErrorReporter(rollbar)
    // `@curbside-health-inc/pathway-v2` keeps its own reporter slot; same instance.
    registerPathwayV2ErrorReporter(rollbar)
    return () => {
      registerErrorReporter(null)
      registerPathwayV2ErrorReporter(null)
    }
  }, [rollbar])

  return null
}

const App: React.FC = () => (
  <Provider config={rollbarConfig}>
    <ReportCaughtErrors />
    <ApolloProvider client={apolloClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={cecmDefaultTheme}>
          <CssBaseline />
          <GlobalStyles styles={MuiSevenGlobalStyles} />
          {/*
            Bottom-right, which is also `ToastProvider`'s own default. Bottom-left
            is where the sidebar pins "Invite People", so every toast landed on
            top of a persistent control.
          */}
          <ToastProvider placement='bottom-right'>
            <Router>
              <ServerHealth>
                <CurbsideRouter />
              </ServerHealth>
            </Router>
          </ToastProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ApolloProvider>
  </Provider>
)

export default App
